import { Select } from "@radix-ui/themes";
import {
  ANNOTATION_TYPES,
  CAMERA_MODES,
} from "@/entities/annotation/model/consts";
import {
  selectAnnotationData,
  selectAnnotationView,
  selectCurrentCamera,
  selectIsSkuSectionPlanogramVisible,
  selectSelectedCameraMode,
  selectGondolaDropdownValue,
  selectSelectedOrder,
} from "@/entities/annotation/model/slices/slice";
import { useAppSelector } from "@/app/appStore";
import { useCameras } from "../hooks/useCameras";
import { type FC, useMemo } from "react";

interface Camera {
  id: string;
}

interface CameraGroup {
  id: string;
  label: string;
  cameras: Camera[];
}

interface FlatCameraConfig {
  cameras: Camera[];
  cameraGroups?: never;
}

interface GroupedCameraConfig {
  cameras?: never;
  cameraGroups: CameraGroup[];
}

type CameraConfig = FlatCameraConfig | GroupedCameraConfig;

interface DropdownConfig {
  planogramView: FlatCameraConfig;
  gondolaAll: GroupedCameraConfig;
  shelf: FlatCameraConfig;
  hand: FlatCameraConfig;
  all: FlatCameraConfig;
}

interface CameraDropdownProps {
  onCameraSelect: (cameraId: string) => void;
}

const CameraDropdown: FC<CameraDropdownProps> = ({ onCameraSelect }) => {
  const annotationView = useAppSelector(selectAnnotationView);
  const annotationData = useAppSelector(selectAnnotationData);
  const gondolaDropdownValue = useAppSelector(selectGondolaDropdownValue);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const selectedCamera = useAppSelector(selectCurrentCamera);
  const selectedCameraMode = useAppSelector(selectSelectedCameraMode);
  const isPlanogramViewVisible = useAppSelector(
    selectIsSkuSectionPlanogramVisible
  );

  const cameraData = useCameras();

  const isProximityTimeRangesAnnotation = useMemo(
    () =>
      annotationView === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES &&
      !!annotationData,
    [annotationView, annotationData]
  );

  const dropdownConfig = useMemo<DropdownConfig>(() => {
    const handCameras = cameraData?.gondolaCameras?.handTracking || [];
    const shelfCameras = cameraData?.gondolaCameras?.shelfTracking || [];

    // Find cameras that exist in both arrays (dual-mode cameras)
    const dualModeCameraIds = new Set(
      handCameras
        .filter((handCamera) =>
          shelfCameras.some((shelfCamera) => shelfCamera.id === handCamera.id)
        )
        .map((camera) => camera.id)
    );

    const handOnlyCameras = handCameras.filter(
      (camera) => !dualModeCameraIds.has(camera.id)
    );
    const shelfOnlyCameras = shelfCameras.filter(
      (camera) => !dualModeCameraIds.has(camera.id)
    );
    const dualModeCameras = handCameras.filter((camera) =>
      dualModeCameraIds.has(camera.id)
    );

    return {
      planogramView: {
        cameras: shelfCameras,
      },
      gondolaAll: {
        cameraGroups: [
          ...(dualModeCameras.length > 0
            ? [
                {
                  id: "dual-mode-group",
                  label: "Hand/Shelf Tracking Cameras",
                  cameras: dualModeCameras,
                },
              ]
            : []),
          ...(handOnlyCameras.length > 0
            ? [
                {
                  id: "hand-tracking-group",
                  label: "Hand Tracking Cameras",
                  cameras: handOnlyCameras,
                },
              ]
            : []),
          ...(shelfOnlyCameras.length > 0
            ? [
                {
                  id: "shelf-tracking-group",
                  label: "Shelf Tracking Cameras",
                  cameras: shelfOnlyCameras,
                },
              ]
            : []),
        ],
      },
      shelf: {
        cameras: shelfCameras,
      },
      hand: {
        cameras: handCameras,
      },
      all: {
        cameras: cameraData?.allCameras || [],
      },
    };
  }, [cameraData]);

  const { config, isDisabled } = useMemo(() => {
    let selectedConfig: CameraConfig = dropdownConfig.all;
    if (isPlanogramViewVisible) {
      selectedConfig = dropdownConfig.planogramView;
    } else if (
      gondolaDropdownValue?.id !== -1 &&
      selectedCameraMode === CAMERA_MODES.ALL
    ) {
      selectedConfig = dropdownConfig.gondolaAll;
    } else if (selectedCameraMode === CAMERA_MODES.SHELF) {
      selectedConfig = dropdownConfig.shelf;
    } else if (selectedCameraMode === CAMERA_MODES.HAND) {
      selectedConfig = dropdownConfig.hand;
    }

    const disabled = !selectedOrder || isProximityTimeRangesAnnotation;

    if (
      import.meta.env.DEV ||
      new URLSearchParams(location.search).get("enable_camera_0")
    ) {
      const randomCamera = selectedConfig.cameras?.[0];

      if (randomCamera) {
        selectedConfig.cameras?.push({
          ...randomCamera,
          id: "0",
        });
      }
    }

    return {
      config: selectedConfig,
      isDisabled: disabled,
    };
  }, [
    isPlanogramViewVisible,
    gondolaDropdownValue?.id,
    selectedCameraMode,
    selectedOrder,
    isProximityTimeRangesAnnotation,
    dropdownConfig,
  ]);

  if (!selectedOrder) {
    return (
      <Select.Root value="" size="1" disabled>
        <Select.Trigger />
        <Select.Content />
      </Select.Root>
    );
  }

  return (
    <Select.Root
      value={selectedCamera}
      size="1"
      disabled={isDisabled}
      onValueChange={onCameraSelect}
    >
      <Select.Trigger className="w-14" />
      <Select.Content>
        {"cameraGroups" in config
          ? config.cameraGroups?.map((group: CameraGroup) => (
              <Select.Group key={group.id}>
                <Select.Label>{group.label}</Select.Label>
                {group.cameras.map((camera: Camera) => (
                  <Select.Item key={camera.id} value={camera.id}>
                    {camera.id}
                  </Select.Item>
                ))}
              </Select.Group>
            ))
          : config.cameras.length > 0 &&
            config.cameras.map((camera: Camera) => (
              <Select.Item key={camera.id} value={camera.id}>
                {camera.id}
              </Select.Item>
            ))}
      </Select.Content>
    </Select.Root>
  );
};

export default CameraDropdown;
